<template>
  <div>
    <h4>{{ $t('creator.followersByUser') }}</h4>
    <b-card>
      <!-- Skeleton -->
      <div v-if="loading" class="d-flex flex-wrap justify-content-center" :class="{'flex-md-nowrap': !is_in_sidebar}">
      
        <div class="d-flex flex-column align-items-center justify-content-center flex-shrink-0" style="width:275px; height:275px;">
          <b-skeleton type="avatar" size="220px" />
        </div>

        <div class="ml-2 w-100">
          <div class="followers-list">
            <div
              v-for="i in 10"
              :key="i"
              class="followers-list__item d-flex align-items-center mb-75"
            >
              <b-skeleton type="avatar" size="28px" class="mr-75 flex-shrink-0" />
              <div class="followers-list__item-content d-flex flex-column overflow-hidden">
                <b-skeleton width="70%" height="14px" class="mb-25" />
                <b-skeleton width="50%" height="12px" />
              </div>
            </div>
          </div>
        </div>
      </div>

      <div v-else class="d-flex flex-wrap justify-content-center" :class="{'flex-md-nowrap': !is_in_sidebar}">
  
        <!-- Donut chart -->
        <div class="d-flex flex-column justify-content-start align-items-start justify-content-between h-100 flex-shrink-0">
          <component
            :is="apex_charts"
            class="cursor-pointer"
            height="275"
            :options="options_network_apex"
            :series="series_network"
            v-if="change_network"
          />
        </div>
  
        <!-- Followers list -->
        <div class="ml-2 w-100">
          <div class="followers-list">
            <div
              v-for="item in followers_by_username"
              :key="item.uuid"
              class="followers-list__item d-flex align-items-center mb-75"
            >
              <div
                class="network-avatar d-flex align-items-center justify-content-center rounded-circle mr-75 flex-shrink-0"
                :style="{ backgroundColor: getColor(item.network, true) }"
              >
                <img
                  v-if="getNetworkIcon(item.network)"
                  :src="getNetworkIcon(item.network)"
                  width="14"
                  height="14"
                  :alt="item.network"
                />
              </div>
    
              <div class="followers-list__item-content d-flex flex-column overflow-hidden">
                <span class="followers-list__username font-weight-bold text-truncate">
                  {{ getUsernameFormattingForDonut(item) }}
                </span>
                <span class="followers-list__followers text-muted">
                  {{ getFormat(item.followers) }} {{ $t('lists.followers') }}
                </span>
              </div>
            </div>
          </div>
        </div>
  
      </div>
    </b-card>
  </div>
</template>

<script>
import { BCard, BSkeleton } from 'bootstrap-vue'
import { formatNetworkDonutData, getUsernameFormattingForDonut } from '@/domain/proposal/mapper/proposal-mapper';
import { getFormat } from '@/libs/utils/formats';
import { getDropdownNetworks, getColor } from '@/libs/utils/icons';

export default {
  name: 'DonutMetrics',
  components: {
    BCard,
    BSkeleton,
  },
  async mounted() {
    this.apex_charts = (await import('vue-apexcharts')).default;
  },
  data() {
    return {
      apex_charts: null,
      change_network: false,
      options_network_apex: {
        colors: [],
        labels: [],
        total: 0,
        chart: {
          type: 'donut',
          toolbar: {
            show: false,
          },
        },
        yaxis: {
          labels: {
            formatter(val) {
              return `${getFormat(val)}`
            },
          },
        },
        dataLabels: {
          enabled: false,
        },
        legend: { show: false },
        comparedResult: [2, -3, 8],
        stroke: { width: 0 },
        plotOptions: {
          pie: {
            startAngle: 0,
            donut: {
              labels: {
                show: true,
                name: {
                  offsetY: 15,
                },
                value: {
                  offsetY: -15,
                  formatter(val) {
                    return `${getFormat(val)}`
                  },
                },
                total: {
                  show: true,
                  offsetY: 15,
                  label: this.$t('creator.totalAudience'),
                  formatter: () => { return `${getFormat(this.options_network_apex.total)}` },
                },
              },
            },
          },
        },
      },
      series_network: [],
    }
  },
  props: {
    loading: {
      type: Boolean,
      default: true,
    },
    networks: {
      type: Object,
      default: () => ({}),
    },
    followers_by_username: {
      type: Array,
      default: () => ([]),
    },
    is_in_sidebar: {
      type: Boolean,
      default: false,
    },
  },
  created() {
    if (this.networks && Object.keys(this.networks).length > 0) {
      this.formatData();
    }
  },
  methods: {
    getFormat,
    getColor,
    getUsernameFormattingForDonut,
    getNetworkIcon(network) {
      const found = getDropdownNetworks().find(n => n.value === network);
      return found ? found.icon : null;
    },
    formatData() {
      const { labels, series, colors, total } = formatNetworkDonutData(this.networks);

      this.options_network_apex.colors = colors;
      this.options_network_apex.labels = labels;
      this.options_network_apex.total = total;
      this.series_network = series;
      this.change_network = true;
    },
    
  },
  watch: {
    networks(newVal) {
      if (newVal && Object.keys(newVal).length > 0) {
        this.formatData();
      }
    },
  },
};
</script>
<style lang="scss" scoped>
.followers-list {
  .mb-25 {
    margin-bottom: 0.25rem;
  }

  max-height: 275px;
  overflow-y: auto;
  flex: 1;
  padding-right: 4px;
  display: grid;
  grid-template-columns: 1fr 1fr;
  align-content: start;
  gap: 0 12px;
  

  &::-webkit-scrollbar {
    width: 4px;
  }
  &::-webkit-scrollbar-thumb {
    background: #e0e0e0;
    border-radius: 4px;
  }

  &__item {
    min-width: 0;
    overflow: hidden;
  }

  &__item-content {
    min-width: 0;
    flex: 1;
  }

  &__username {
    font-size: 0.9rem;
  }

  &__followers {
    font-size: 0.82rem;
  }
}

.network-avatar {
  width: 28px;
  height: 28px;
  min-width: 28px;
}
</style>